import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthLayout } from "./AuthLayout";
import { Button, Spinner, TextField, Callout } from "@/design/components";
import { resendVerification, verifyEmail } from "@/api/auth";
import { ApiError } from "@/api/client";
import authStyles from "./AuthForm.module.css";
import styles from "./VerifyScreen.module.css";

const resendSchema = z.object({ email: z.string().trim().email("Enter a valid email") });
type ResendValues = z.infer<typeof resendSchema>;

/** Re-request a verification link (shown when a link is expired/invalid). The token
 *  carries no email, so the user supplies it. Enumeration-safe: success is generic. */
function ResendVerification() {
  const [sent, setSent] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const sentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (sent) sentRef.current?.focus();
  }, [sent]);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResendValues>({ resolver: zodResolver(resendSchema), defaultValues: { email: "" } });

  const onSubmit = handleSubmit(async ({ email }) => {
    setFormError(null);
    try {
      await resendVerification(email);
      setSent(true);
    } catch {
      setFormError("Couldn't send the email. Please try again.");
    }
  });

  if (sent) {
    return (
      <div ref={sentRef} tabIndex={-1} className={authStyles.focusResult}>
        <Callout tone="success" title="Check your inbox">
          If that email has an unverified Cogent account, a new verification link is on its way.
        </Callout>
      </div>
    );
  }

  return (
    <form className={authStyles.form} onSubmit={onSubmit} noValidate>
      {formError && (
        <div className={authStyles.formError} role="alert">
          {formError}
        </div>
      )}
      <TextField
        label="Your email"
        type="email"
        autoComplete="email"
        placeholder="you@company.com"
        required
        error={errors.email?.message}
        {...register("email")}
      />
      <Button type="submit" size="lg" fullWidth loading={isSubmitting}>
        Resend verification email
      </Button>
    </form>
  );
}

export function VerifyScreen() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const { isPending, isError, isSuccess, data, error } = useQuery({
    queryKey: ["verify", token],
    queryFn: () => verifyEmail(token),
    enabled: token.length > 0,
    retry: false,
  });

  // This screen verifies automatically on mount (no user click), so when the query
  // settles, move focus to the result region — otherwise a keyboard/SR user is
  // stranded at the document top after the silent "Verifying…" → result transition.
  const resultRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isSuccess || isError) resultRef.current?.focus();
  }, [isSuccess, isError]);

  return (
    <AuthLayout>
      <header className={authStyles.head}>
        <p className="u-eyebrow">Email verification</p>
        <h2 className={authStyles.title}>
          {isSuccess ? "You're verified" : isError || !token ? "Verification failed" : "Verifying…"}
        </h2>
      </header>

      <div className={[styles.body, authStyles.focusResult].join(" ")} ref={resultRef} tabIndex={-1}>
        {!token && (
          <Callout tone="danger" title="Missing token">
            This verification link is incomplete. Use the most recent link from your email.
          </Callout>
        )}

        {token && isPending && (
          <div className={styles.center}>
            <Spinner size={28} color="var(--accent)" />
            <p className={styles.muted}>Confirming your link…</p>
          </div>
        )}

        {isSuccess && (
          <>
            <Callout tone="success" title="Email confirmed">
              {data.email} is verified. You can sign in to your control plane now.
            </Callout>
            <Button size="lg" fullWidth onClick={() => navigate("/login", { replace: true })}>
              Continue to sign in
            </Button>
          </>
        )}

        {token && isError && (
          <>
            <Callout tone="danger" title="We couldn't verify this link">
              {error instanceof ApiError
                ? error.message
                : "The link may have expired or already been used."}
            </Callout>
            <p className={styles.muted}>Enter your email to get a fresh verification link.</p>
            <ResendVerification />
            <p className={authStyles.alt}>
              <Link to="/login">Back to sign in</Link>
            </p>
          </>
        )}
      </div>
    </AuthLayout>
  );
}
